import { addEventListener, } from 'vevet-dom';
import { Callbacks } from './Callbacks';
import { MutableProp } from './MutableProp';
import mergeWithoutArrays from '../utils/common/mergeWithoutArrays';
import { throwVevetAppError } from '../utils/errors';
/**
 * A class for modules.
 */
export class Module {
    /**
     * @example
     * const mod = new Module();
     */
    constructor(
    /**
     * Properties on script start
     */
    initialProp, 
    /**
     * Defines if you need to call {@linkcode Module.init} at the constructor's end.
     * If you want to add responsive properties, set this argument to FALSE.
     */
    init = true) {
        /**
         * If the module is initialized
         */
        this._inited = false;
        /**
         * Viewport callbacks
         */
        this._viewportCallbacks = [];
        // set vars
        if (window.vevetApp) {
            this._app = window.vevetApp;
        }
        else {
            throwVevetAppError();
        }
        // set default vars
        this._destroyed = false;
        this._listeners = [];
        // create callbacks
        this._callbacks = new Callbacks();
        // create mutable properties
        const prop = mergeWithoutArrays(this._getDefaultProp(), initialProp || {});
        this._mutableProp = new MutableProp(prop, this._onPropResponsive.bind(this), this._onPropChange.bind(this), this.name);
        if (init) {
            this.init();
        }
    }
    /**
     * Get Default properties (should be extended)
     */
    _getDefaultProp() {
        return {
            parent: false,
        };
    }
    /**
     * Current properties
     */
    get prop() {
        return this._mutableProp.prop;
    }
    /**
     * Module Callbacks
     */
    get callbacks() {
        return this._callbacks;
    }
    /**
     * Module prefix
     */
    get prefix() {
        return '';
    }
    /**
     * Get module name
     */
    get name() {
        return this.constructor.name;
    }
    get inited() {
        return this._inited;
    }
    get destroyed() {
        return this._destroyed;
    }
    /**
     * Add responsive rules
     */
    addResponsiveProp(rules) {
        if (this._inited) {
            throw new Error('Responsive properties cannot be added because the class instance is already initialized');
        }
        else {
            this._mutableProp.addResponsiveProp(rules);
        }
    }
    /**
     * Change module properties.
     * @example
     *
     * // changing properties
     * // let's imagine that the module has the following properties:
     * prop = {
     *     name: 'module',
     *     cute: true
     * };
     * // we can change some properties in it: whether one or several properties
     * // after the properties are changed, the method _onPropChange is called.
     * module.changeProp({
     *     cute: false
     * });
     */
    changeProp(prop = {}) {
        this._mutableProp.changeProp(prop);
        this._callbacks.tbt('changeProp', false);
    }
    /**
     * The method that is called on window resize and properties change.
     */
    _onPropResponsive() {
        this._onPropMutate();
    }
    /**
     * The method that is called on properties change.
     */
    _onPropChange(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    changedProp) {
        this._onPropMutate();
    }
    /**
     * The method that is called on properties change.
     */
    _onPropMutate() { }
    /**
     * Initializes the class.
     */
    init() {
        // return if the module is already initialized
        if (this._inited) {
            return;
        }
        this._inited = true;
        // continue initializing
        this._constructor();
        this._setEvents();
        // destroy the current module on parent destroy
        if (this.prop.parent) {
            this.prop.parent.addCallback('destroy', () => {
                this.destroy();
            });
        }
    }
    /**
     * Extra constructor
     */
    _constructor() {
        // code
    }
    /**
     * Set events
     */
    _setEvents() {
        // code
    }
    /**
     * Add a viewport callback that will be removed on class destroy
     * {@see Viewport}
     */
    addViewportCallback(target, func, data = {}) {
        const callback = this._app.viewport.add(target, func, Object.assign(Object.assign({}, data), { name: this.constructor.name }));
        this._viewportCallbacks.push(callback);
        return callback;
    }
    /**
     * Add a module callback
     * {@see Callbacks}
     */
    addCallback(target, func, data = {}) {
        const callback = this.callbacks.add(target, func, data);
        return callback;
    }
    /**
     * Add a DOM event listeners
     */
    addEventListeners(el, target, callback, options) {
        const listener = addEventListener(el, target, callback, options);
        this._listeners.push(listener);
        return listener;
    }
    /**
     * Destroy the module
     */
    destroy() {
        if (this.destroyed) {
            return;
        }
        this._destroy();
    }
    /**
     * Destroy the module
     */
    _destroy() {
        // destroy callbacks
        this._callbacks.tbt('destroy', false);
        this._callbacks.destroy();
        // destroy mutable properties
        this._mutableProp.destroy();
        // destroy viewport callbacks
        this._viewportCallbacks.forEach((callback) => {
            callback.remove();
        });
        // destroy all listeners
        this._listeners.forEach((listener) => {
            listener.remove();
        });
        // events
        this._destroyed = true;
    }
}
